import { HtmlParser } from '@calendly/html-parser'
import { parseQuery } from '@calendly/http-tools'
import { I18n } from '@calendly/i18n'
import { Loader } from '@calendly/ui/components/loader'
import { Body, Footer } from '@calendly/ui/components/popup'
import { BodyText, HeadingText } from '@calendly/ui/components/typography'
import { useDeviceType } from '@calendly/ui/hooks/use-device-type'
import { useEffect, useState, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { useTrackTrialExtended } from 'hooks/analytics_events'
import { useMeetingTypesPath } from 'hooks/use_meeting_types_path.js'
import minimumPlanHeaderImage from 'images/billing/mvplan_header.svg'
import minimumPlanLeftImage from 'images/billing/mvplan_left.svg'

import {
  trialExtensionLoadingSelector,
  extendedDaysSelector,
  isErrorStateSelector
} from './selectors'
import {
  WarningBanner,
  Container,
  Image,
  TextContainer,
  SpacedText,
  StyledButton
} from './styles'
import { extendTrial } from '../actions/extend_trial'
import { useBillingPath } from '../hooks/use_billing_path.js'


export const TrialExtension = () => {
  const dispatch = useDispatch()
  const [extraDays, setExtraDays] = useState(null)
  const isLoading = useSelector(trialExtensionLoadingSelector)
  const isErrorState = useSelector(isErrorStateSelector)
  const extendedDays = useSelector(extendedDaysSelector)
  const isTabletUp = useDeviceType('isTabletUp')
  const billingHomePath = useBillingPath()
  const imageSource = isTabletUp ? minimumPlanLeftImage : minimumPlanHeaderImage
  const meetingTypesPath = useMeetingTypesPath()
  const trackTrialExtended = useTrackTrialExtended()
  const hasTrackedRef = useRef(false)

  useEffect(() => {
    const params = parseQuery(window.location.search)
    if (params.extra_days) {
      setExtraDays(params.extra_days)
    } else {
      window.location.assign(billingHomePath)
    }
  }, [billingHomePath])

  useEffect(() => {
    const extend = async () => {
      if (extraDays && !hasTrackedRef.current) {
        hasTrackedRef.current = true
        try {
          const response = await dispatch(extendTrial({ extraDays })).unwrap()
          trackTrialExtended({
            days_to_add: response.days_to_add,
            new_trial_date_end: response.new_trial_date_end,
            trial_id: response.trial_id,
          })
        } catch {
          // Error state is handled by the selector
        }
      }
    }
    extend()
  }, [dispatch, extraDays, trackTrialExtended])

  if (isLoading) return <Loader />

  if (isErrorState) {
    return (
      <WarningBanner>
        <BodyText>
          {HtmlParser(I18n.t('publisher.billing.trial_extension.error'))}
        </BodyText>
      </WarningBanner>
    )
  }

  return (
    <Container>
      <Image src={imageSource} alt="" />
      <TextContainer>
        <Body>
          <HeadingText level={1}>
            {I18n.t('publisher.billing.trial_extension.title', { extendedDays })}
          </HeadingText>
          <SpacedText>
            {I18n.t('publisher.billing.trial_extension.body')}
          </SpacedText>
        </Body>
        <Footer>
          <StyledButton onClick={() => { window.location.assign(meetingTypesPath) }}>
            {I18n.t('publisher.billing.trial_extension.continue_home')}
          </StyledButton>
        </Footer>
      </TextContainer>
    </Container>
  )
}
